import React, { useState, useEffect } from 'react';
import { FiFileText, FiFile, FiDownload, FiSearch, FiBook } from 'react-icons/fi';
import { materialsAPI, API_ORIGIN } from '../../services/api';
import './StudentMaterials.css';
import { useNavigate } from 'react-router-dom';

const StudentMaterials = () => {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadMaterials();
    }, []);

    const loadMaterials = async () => {
        try {
            setLoading(true);
            const response = await materialsAPI.getStudentMaterials();
            setMaterials(response.data.materials || []);
        } catch (error) {
            console.error('Ошибка загрузки материалов:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredMaterials = materials.filter(m => {
        const matchesFilter = filter === 'all' || m.type === filter;
        const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase()));
        return matchesFilter && matchesSearch;
    });

    if (loading) {
        return <div className="student-materials-loading">Загрузка...</div>;
    }

    if (selectedMaterial) {
        return (
            <div className="student-material-detail">
                <div className="material-detail-header">
                    <button 
                        onClick={() => setSelectedMaterial(null)}
                        className="btn-back-material"
                    >
                        ← Назад к списку
                    </button>
                    <h1>{selectedMaterial.title}</h1>
                </div>

                {selectedMaterial.description && (
                    <div className="material-detail-description">
                        {selectedMaterial.description}
                    </div>
                )}

                {selectedMaterial.type === 'text' && (
                    <div 
                        className="material-detail-content"
                        dangerouslySetInnerHTML={{ __html: selectedMaterial.content }}
                    />
                )}

                {selectedMaterial.type === 'file' && selectedMaterial.file_url && (
                    <div className="material-detail-file">
                        <div className="file-info-box">
                            <FiFile size={48} color="#52c41a" />
                            <p>Файл для скачивания</p>
                            <a
                                href={`${API_ORIGIN}${selectedMaterial.file_url}`}
                                download
                                className="btn-download-file"
                            >
                                <FiDownload />
                                Скачать файл
                            </a>
                        </div>
                    </div>
                )}

                {selectedMaterial.type === 'lesson' && (
                    <div className="material-detail-file">
                        <div className="file-info-box">
                            <FiBook size={48} color="#4a90e2" />
                            <p>Это урок-материал</p>
                            <button
                                type="button"
                                onClick={() => navigate(`/student/materials/lesson/${selectedMaterial.id}`)}
                                className="btn-download-file"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                            >
                                Открыть урок
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="student-materials-page">
            <div className="student-materials-header">
                <div>
                    <h1>Материалы</h1>
                    <p>Дополнительные материалы для изучения</p>
                </div>
            </div>

            {/* Поиск и фильтры */}
            <div className="materials-controls">
                <div className="search-box">
                    <FiSearch />
                    <input
                        type="text"
                        placeholder="Поиск материалов..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                
                <div className="materials-filters-student">
                    <button 
                        className={`filter-btn-student ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        Все
                    </button>
                    <button 
                        className={`filter-btn-student ${filter === 'text' ? 'active' : ''}`}
                        onClick={() => setFilter('text')}
                    >
                        <FiFileText />
                        Текстовые
                    </button>
                    <button 
                        className={`filter-btn-student ${filter === 'file' ? 'active' : ''}`}
                        onClick={() => setFilter('file')}
                    >
                        <FiFile />
                        Файлы
                    </button>
                    <button 
                        className={`filter-btn-student ${filter === 'lesson' ? 'active' : ''}`}
                        onClick={() => setFilter('lesson')}
                    >
                        <FiBook />
                        Уроки
                    </button>
                </div>
            </div>

            {filteredMaterials.length === 0 ? (
                <div className="empty-materials-student">
                    <p>
                        {searchQuery 
                            ? 'Материалы не найдены'
                            : 'Материалов пока нет'
                        }
                    </p>
                </div>
            ) : (
                <div className="student-materials-grid">
                    {filteredMaterials.map(material => (
                        <div 
                            key={material.id} 
                            className="student-material-card"
                            onClick={() => {
                                if (material.type === 'lesson') {
                                    navigate(`/student/materials/lesson/${material.id}`);
                                    return;
                                }
                                setSelectedMaterial(material);
                            }}
                        >
                            <div className="student-material-icon">
                                {material.type === 'text' ? (
                                    <FiFileText size={32} color="#4a90e2" />
                                ) : material.type === 'lesson' ? (
                                    <FiBook size={32} color="#4a90e2" />
                                ) : (
                                    <FiFile size={32} color="#52c41a" />
                                )}
                            </div>
                            
                            <div className="student-material-info">
                                <h3>{material.title}</h3>
                                {material.description && (
                                    <p className="student-material-description">
                                        {material.description.substring(0, 100)}
                                        {material.description.length > 100 ? '...' : ''}
                                    </p>
                                )}
                                <div className="student-material-meta">
                                    <span className="student-material-type">
                                        {material.type === 'text' ? 'Текстовый материал' : material.type === 'lesson' ? 'Урок-материал' : 'Файл'}
                                    </span>
                                    <span className="student-material-date">
                                        {new Date(material.created_at).toLocaleDateString('ru-RU')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StudentMaterials;
